import 'core-js/actual/structured-clone';

import { ApolloProvider, useConfiguredApolloClient } from '@sixfold/app-data-framework';
import {
  ErrorHandlerProvider,
  lazyWithRetry,
  useBooleanLocalStorage,
  useConstant,
  useErrorHandler,
} from '@sixfold/app-utils';
import {
  LocalizationDictionary,
  LocalizationProvider,
  NotificationBoundary,
  SpinnerOverlay,
  ToastProvider,
} from '@sixfold/common-ui';
import { i18n, initialize as initializeI18n, useLanguage, useLocalizations } from '@sixfold/localization-component';
import { notNil } from '@sixfold/typed-primitives';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';
import { Provider as JotaiProvider } from 'jotai';
import {
  FunctionComponent,
  PropsWithChildren,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter as Router } from 'react-router';

import { App } from './app';
import { AnalyticsProvider } from './components/analytics';
import { EmbedDataContext, EmbedDataProvider } from './components/embed_data_context';
import { ErrorBoundary } from './components/error_boundary';
import { supportedLanguages } from './components/language_picker';
import { TrackingConsentPopup } from './components/tracking_consent_popup';
import { UpdateNotifier } from './components/update_notifier';
import { WelcomeLanguageModal } from './components/welcome_language_modal';
import { possibleTypes, typePolicies } from './lib/apollo_client/utils';
import { getEmbedConfig, getEmbedData } from './lib/data';
import { getIsConsentGiven } from './lib/util/cookie_consent';
import { RouterProvider } from './router_provider';
import { sentryOptions } from './sentry';
import { ThemeWrapper } from './theme_wrapper';
import { useUserProfile } from './user';

// import after ThemeWrapper brings in common-ui reset and other assets
import './styles/main.css';

const ShareRoute = lazyWithRetry(() => import('./routes/share').then(({ ShareRoute }) => ({ default: ShareRoute })));

const PublicSharedViewRoute = lazyWithRetry(() =>
  import('./routes/public_shared_view').then(({ PublicSharedViewRoute }) => ({ default: PublicSharedViewRoute })),
);

const VisibilityAgentFloatingButton = lazyWithRetry(() =>
  import('./visibility_agent').then(({ VisibilityAgentFloatingButton }) => ({
    default: VisibilityAgentFloatingButton,
  })),
);

const VisibilityAgentPanel = lazyWithRetry(() =>
  import('./visibility_agent').then(({ VisibilityAgentPanel }) => ({ default: VisibilityAgentPanel })),
);

const embedConfig = getEmbedConfig();
const embedData = getEmbedData();

initializeI18n({
  detectBrowserLanguage: {
    cookieDomain: embedConfig !== null ? embedConfig.cookie_domain : undefined,
    order: ['querystring', 'cookie', 'navigator', 'htmlTag'],
  },
  supportedLanguages: Object.keys(supportedLanguages),
  nonExplicitSupportedLanguages: true,
  logMissingKeys: false,
  useHttpBackend: {
    loadPath: (lng: readonly string[], ns: readonly string[]) =>
      `/locales/${lng[0]}/${ns[0]}?key=${embedData.translations_cache_keys?.[lng[0]] ?? ''}`,
  },
});

calculateScrollbarWidth();

const UserLocaleSync: FunctionComponent<PropsWithChildren<unknown>> = () => {
  const user = useUserProfile();
  const language = useLanguage();

  useEffect(() => {
    if (notNil(user) && notNil(user?.locale) && user?.locale !== language) {
      i18n.changeLanguage(user.locale).catch((e) => console.error('Failed to change user locale', e));
    }
  }, [language, user]);

  // We don't render anything
  return null;
};

const WelcomeLanguageModalContainer: FunctionComponent = () => {
  const user = useUserProfile();

  if (user === undefined) {
    return null;
  }

  return <WelcomeLanguageModal userId={user.user_id} userLocale={user.locale} />;
};

const AppWrapper: FunctionComponent<
  PropsWithChildren<{
    isShareLink?: boolean;
    isSharedView?: boolean;
    logOut: () => void;
  }>
> = ({ logOut, isShareLink, isSharedView }) => {
  const { embedData } = useContext(EmbedDataContext);
  const [isAnalyticsConsented, setIsAnalyticsConsented] = useState<boolean>(getIsConsentGiven());
  const [isVisibilityAgentOpen, setIsVisibilityAgentOpen] = useState<boolean>(false);
  const showApp = !isShareLink && !isSharedView;
  const user = useUserProfile();
  const isVisibilityAgentEnabled = user?.isVisibilityAgentEnabled ?? false;

  const commonUIlocalizations = useLocalizations({
    'common.ui.clear.title': 'Clear',
    'common.ui.close.title': 'Close',
    'common.ui.remove.title': 'Remove',
    'common.ui.sort.ascending.title': 'Sort ascending',
    'common.ui.sort.descending.title': 'Sort descending',
    'common.ui.table.selectRow': 'Select',
    'common.ui.table.selectAllRows': 'Select all',
    'common.ui.table.dragHandleLabel': 'Drag and drop to reorder columns',
    'common.ui.tree.expandItem': 'Expand',
    'common.ui.toast.progressLine': 'Progress line',
    'common.ui.treeSelect.search': 'Search',
    'component.filter.multiselect.noResults.label': 'We could not find any results for this search',
    'common.ui.treeSelect.search.checkAndTryAgain': 'Please check your search keyword and try again.',
    'common.ui.multiSelect.create': 'Create',
    'common.ui.multiSelect.select': 'Select',
    'common.ui.timePicker.time': 'Time',
    'common.ui.timePicker.minutes': 'Minutes',
    'common.ui.timePicker.hours': 'Hours',
    'common.ui.timePicker.addMinutes': 'Add minutes',
    'common.ui.timePicker.subMinutes': 'Subtract minutes',
    'common.ui.timePicker.addHours': 'Add Hours',
    'common.ui.timePicker.subHours': 'Subtract hours',
    'common.ui.timePicker.apply': 'Apply',
    'common.ui.timePicker.yourSelection': 'Your selection',
  });
  const localizationProviderDictionary: LocalizationDictionary = useMemo(
    () => ({
      clear: commonUIlocalizations['common.ui.clear.title'],
      close: commonUIlocalizations['common.ui.close.title'],
      remove: commonUIlocalizations['common.ui.remove.title'],
      sortAscending: commonUIlocalizations['common.ui.sort.ascending.title'],
      sortDescending: commonUIlocalizations['common.ui.sort.descending.title'],
      'table.selectRow': commonUIlocalizations['common.ui.table.selectRow'],
      'table.selectAllRows': commonUIlocalizations['common.ui.table.selectAllRows'],
      'tree.expandItem': commonUIlocalizations['common.ui.tree.expandItem'],
      search: commonUIlocalizations['common.ui.treeSelect.search'],
      progressLine: commonUIlocalizations['common.ui.toast.progressLine'],
      'search.noResults': commonUIlocalizations['component.filter.multiselect.noResults.label'],
      'search.checkAndTryAgain': commonUIlocalizations['common.ui.treeSelect.search.checkAndTryAgain'],
      'multiSelect.create': commonUIlocalizations['common.ui.multiSelect.create'],
      'multiSelect.select': commonUIlocalizations['common.ui.multiSelect.select'],
      'table.dragHandleLabel': commonUIlocalizations['common.ui.table.dragHandleLabel'],
      'timePicker.time': commonUIlocalizations['common.ui.timePicker.time'],
      'timePicker.minutes': commonUIlocalizations['common.ui.timePicker.minutes'],
      'timePicker.hours': commonUIlocalizations['common.ui.timePicker.hours'],
      'timePicker.addMinutes': commonUIlocalizations['common.ui.timePicker.addMinutes'],
      'timePicker.subMinutes': commonUIlocalizations['common.ui.timePicker.subMinutes'],
      'timePicker.addHours': commonUIlocalizations['common.ui.timePicker.addHours'],
      'timePicker.subHours': commonUIlocalizations['common.ui.timePicker.subHours'],
      'timePicker.apply': commonUIlocalizations['common.ui.timePicker.apply'],
      'timePicker.yourSelection': commonUIlocalizations['common.ui.timePicker.yourSelection'],
    }),
    [commonUIlocalizations],
  );
  const notificationClassname = useConstant(() => ({
    wrapper: 'notifications',
    container: 'container',
    content: 'content',
  }));

  const shouldRenderAnalytics = embedData.is_share_link !== true || embedData.auth_token !== undefined;
  const content = (
    <JotaiProvider>
      <Suspense fallback={<SpinnerOverlay />}>
        {showApp && (
          <>
            <UserLocaleSync />
            <WelcomeLanguageModalContainer />
            <App logOut={logOut} />
          </>
        )}
        {isShareLink && (
          <>
            <UpdateNotifier />
            <ShareRoute />
          </>
        )}
        {isSharedView && <PublicSharedViewRoute logOut={logOut} />}

        <TrackingConsentPopup onUserConsentChanged={setIsAnalyticsConsented} />
        {!isShareLink && isVisibilityAgentEnabled && (
          <Suspense fallback={null}>
            <VisibilityAgentFloatingButton
              onClick={() => setIsVisibilityAgentOpen(!isVisibilityAgentOpen)}
              isOpen={isVisibilityAgentOpen}
            />
            <VisibilityAgentPanel isOpen={isVisibilityAgentOpen} onClose={() => setIsVisibilityAgentOpen(false)} />
          </Suspense>
        )}
      </Suspense>
    </JotaiProvider>
  );

  return (
    <Router>
      <RouterProvider>
        <LocalizationProvider dictionary={localizationProviderDictionary}>
          <NotificationBoundary className={notificationClassname}>
            <ToastProvider>
              {shouldRenderAnalytics ? (
                <AnalyticsProvider consented={isAnalyticsConsented}>{content}</AnalyticsProvider>
              ) : (
                content
              )}
            </ToastProvider>
          </NotificationBoundary>
        </LocalizationProvider>
      </RouterProvider>
    </Router>
  );
};

const SessionWrapper: FunctionComponent<PropsWithChildren<unknown>> = () => {
  const { embedConfig, embedData } = useContext(EmbedDataContext);
  const [enablePersistedQueries] = useBooleanLocalStorage('enable_persisted_queries', true);
  const errorHandler = useErrorHandler();
  const authToken = embedData.auth_token;

  const [logOutRedirect, setLogOutRedirect] = useState<string | undefined>(undefined);

  const logOut = useCallback(() => {
    if (embedConfig === null) {
      throw new Error('No config: could not read login_web_url to redirect');
    }

    setLogOutRedirect(`${embedConfig.login_web_url}/logout`);
  }, [embedConfig]);

  const getAuthorizationToken = useCallback(() => authToken, [authToken]);
  const onReportError = useCallback(
    (error: Error, extra?: Record<string, unknown>) => errorHandler?.captureError(error, { extra }),
    [errorHandler],
  );
  const onAuthenticationError = useCallback(() => {
    if (embedConfig === null) {
      throw new Error('No config: could not read login_web_url to redirect');
    }

    setLogOutRedirect(`${embedConfig.login_web_url}?next=${encodeURIComponent(window.location.href)}`);
  }, [setLogOutRedirect, embedConfig]);

  const getHTTPLink = useCallback(
    () =>
      createUploadLink({
        uri: embedConfig?.base_assets_url !== undefined ? `${embedConfig.base_assets_url}/graphql` : '/graphql',
        credentials: 'same-origin',
        headers: { 'apollo-require-preflight': 'true' },
      }),
    [embedConfig?.base_assets_url],
  );

  const apolloClient = useConfiguredApolloClient({
    baseURL: embedConfig?.base_assets_url,
    possibleTypes,
    typePolicies,
    getAuthorizationToken,
    onReportError,
    onAuthenticationError,
    enablePersistedQueries,
    getHTTPLink,
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first',
      },
    },
  });

  if (logOutRedirect !== undefined) {
    window.location.href = logOutRedirect;
    return <SpinnerOverlay />;
  }

  return (
    <ApolloProvider client={apolloClient}>
      <Suspense fallback={null}>
        <ErrorBoundary compact={false}>
          <AppWrapper isShareLink={embedData.is_share_link} isSharedView={embedData.is_shared_view} logOut={logOut} />
        </ErrorBoundary>
      </Suspense>
    </ApolloProvider>
  );
};

const container = document.getElementById('root');
if (!container) throw new Error('Root element not found');

// Dev HMR can evaluate this module again. A second createRoot on the same node
// mounts another copy of the app under the first one.
const rootHost = container as HTMLElement & { __customerWebRoot?: ReturnType<typeof createRoot> };
const root = (rootHost.__customerWebRoot ??= createRoot(container));

root.render(
  <EmbedDataProvider value={{ embedConfig, embedData }}>
    <ErrorHandlerProvider sentryOptions={sentryOptions}>
      <ErrorBoundary compact={false}>
        <ThemeWrapper>
          <ToastProvider>
            <Suspense fallback={<SpinnerOverlay />}>
              <SessionWrapper />
            </Suspense>
          </ToastProvider>
        </ThemeWrapper>
      </ErrorBoundary>
    </ErrorHandlerProvider>
  </EmbedDataProvider>,
);

function calculateScrollbarWidth() {
  const scrollDiv = document.createElement('div');
  scrollDiv.style.position = 'absolute';
  scrollDiv.style.visibility = 'hidden';
  scrollDiv.style.overflow = 'scroll';
  scrollDiv.style.width = '100px';
  scrollDiv.style.height = '100px';
  scrollDiv.style.top = '-9999px';

  document.body.appendChild(scrollDiv);
  const scrollbarWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth;
  document.documentElement.style.setProperty('--calculated-scrollbar-width', `${scrollbarWidth}px`);

  document.body.removeChild(scrollDiv);
}

const removeLoadingSpinner = () => {
  const loadingElement = document.getElementById('loading');
  if (loadingElement && loadingElement.parentNode) {
    loadingElement.parentNode.removeChild(loadingElement);
  }
};

removeLoadingSpinner();
